// @flow
import React from 'react';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';

import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { useEstimateVersion } from '../../../custom-hooks/useEstimateVersion';
import {
  fetchFormValues,
  isAnythingEditedOnNativeInferencePoolPageBesidesTheGivenForm,
  isFormEdited,
  resetValues,
} from '../../cluster/cluster-details-inputs/utils';
import { NATIVE_INFERENCE_POOL_DETAILS_FORM } from '../../../constants';
import { ResetFormButton } from '../../presentational/buttons/ResetFormButton';
import { Spacer } from '../../presentational/spacing/Spacer';
import { SaveFormButton } from '../../presentational/buttons/SaveFormButton';
import { useUpdateNativeInferencePoolMutation } from '../../../service-definitions/estimates';
import { NATIVE_INFERENCE_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP } from '../../../configuration/native-inference-pool-details';
import { toastError } from '../../presentational/notifications/utils';

import { NativeInferencePoolNameContainer } from './NativeInferencePoolNameContainer';
import { NativeInferencePoolProviderContainer } from './NativeInferencePoolProviderContainer';
import { NativeInferencePoolRegionsContainer } from './NativeInferencePoolRegionsContainer';

export const NativeInferencePoolDetailsContainer = () => {
  const { values, isValid, initialValues, resetForm, validateForm } = useFormikContext();
  const { estimateId, nativeInferencePoolId } = useParams();
  const [updateNativeInferencePool] = useUpdateNativeInferencePoolMutation();
  const estimateVersion = useEstimateVersion();

  const isNativeInferencePoolDetailsFormEdited = isFormEdited(
    values,
    initialValues,
    NATIVE_INFERENCE_POOL_DETAILS_FORM,
    NATIVE_INFERENCE_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP
  );
  const disabled = isAnythingEditedOnNativeInferencePoolPageBesidesTheGivenForm(
    values,
    initialValues,
    NATIVE_INFERENCE_POOL_DETAILS_FORM
  );
  const shouldSaveResetBeDisabled = !isValid || disabled || !isNativeInferencePoolDetailsFormEdited;

  return (
    <PaddedAndRaisedSegment disabled={disabled}>
      <span style={{ float: 'left' }}>
        <ResetFormButton
          disabled={disabled || !isNativeInferencePoolDetailsFormEdited}
          onClick={() => {
            resetValues(
              values,
              initialValues,
              resetForm,
              NATIVE_INFERENCE_POOL_DETAILS_FORM,
              NATIVE_INFERENCE_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP
            );
          }}
        />
        <Spacer x={1} />
        <SaveFormButton
          disabled={shouldSaveResetBeDisabled}
          onClick={async () => {
            const errors = await validateForm(values);
            if (Object.keys(errors).length > 0) {
              return;
            }

            const formValuesToSend = fetchFormValues(
              values,
              NATIVE_INFERENCE_POOL_DETAILS_FORM,
              NATIVE_INFERENCE_POOL_DETAILS_FORM_NAMES_TO_FIELDS_MAP
            );
            const payload = {
              id: nativeInferencePoolId,
              estimate_id: estimateId,
              estimate_version: estimateVersion,
              name: formValuesToSend.name,
              inputs: { ...formValuesToSend },
            };

            const { error } = await updateNativeInferencePool({
              estimateId,
              nativeInferencePoolId,
              payload,
            });

            if (error) {
              toastError(error);
            }
          }}
        />
      </span>
      <Spacer y={60} />
      <Grid columns={2} divided={true}>
        <Grid.Row>
          <Grid.Column width={4}>
            <Grid columns={1} divided={true}>
              <Grid.Row>
                <Grid.Column>
                  <NativeInferencePoolNameContainer disabled={disabled} />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
          <Grid.Column width={12}>
            <Grid>
              <Grid.Row>
                <Grid.Column width={5}>
                  <NativeInferencePoolProviderContainer disabled={disabled} />
                </Grid.Column>
                <Grid.Column width={6}>
                  <NativeInferencePoolRegionsContainer disabled={disabled} />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </PaddedAndRaisedSegment>
  );
};
